import { IconCopy } from '@tabler/icons-react';
import type { UsdcWidgetConfig, UsdcWidgetColors } from '../types/widget';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  config: UsdcWidgetConfig;
}

export default function PaymentModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  config,
}: PaymentModalProps) {
  if (!isOpen) return null;

  const handleCopy = async () => {
    if (config.address) {
      await navigator.clipboard.writeText(config.address);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-0 md:p-4">
      <div className="relative w-full h-full md:h-auto md:max-h-[85vh] md:w-[480px] bg-background rounded-none md:rounded-xl overflow-hidden shadow-lg ring-1 ring-surface/5">
        <div className="flex flex-col h-full md:h-auto">
          <div className="flex-1 overflow-y-auto">
            {/* Product Image */}
            <div className="flex flex-col items-center justify-center px-4 md:px-6 pt-8 pb-6">
              <div className="w-full max-w-md bg-surface/10 rounded-2xl p-8">
                {config.productImage && (
                  <img
                    src={config.productImage}
                    alt="Product"
                    className="w-32 h-32 mx-auto rounded-2xl object-contain"
                  />
                )}
                {!config.productImage && config.vendorIcon && (
                  <img
                    src={config.vendorIcon}
                    alt="Vendor"
                    className="w-32 h-32 mx-auto rounded-2xl object-contain"
                  />
                )}
              </div>
            </div>

            {/* Payment Details */}
            <div className="flex flex-col items-center text-center px-4 md:px-6 py-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                {config.title || 'Crypto Payment'}
              </h2>
              <div className="text-4xl font-bold text-primary mb-4">
                {config.currency}{config.amount.toFixed(2)}
              </div>
              <p className="text-text-secondary mb-6">One-time payment</p>

              {/* USDC Address */}
              <div className="w-full max-w-md bg-surface p-4 rounded-xl mb-4 ring-1 ring-surface/10 group transition-all hover:ring-surface/20">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-text-secondary text-sm font-medium">Network</span>
                  <span className="text-white text-sm">Base</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-text-secondary text-sm font-medium">Seller</span>
                  <span className="text-white text-sm font-mono">{config.address.substring(0, 6)}...{config.address.substring(config.address.length - 4)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm font-medium">USDC Address</span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="text-primary hover:text-primary/90 flex items-center gap-2 p-1 rounded-md transition-all"
                  >
                    <IconCopy size={16} />
                    <span className="text-sm">Copy</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="sticky bottom-0 left-0 right-0 p-4 md:p-6 bg-background/80 backdrop-blur-sm border-t border-surface">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-surface text-white font-medium px-6 md:px-8 py-3 rounded-lg hover:bg-surface/80 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="flex-1 bg-primary text-black font-medium px-6 md:px-8 py-3 rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 